/****************************************************
 * Module Name:  memoIssue.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useRef, useState } from "react";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Badge, Box, Button, Checkbox, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import CircularProgress from "@mui/material/CircularProgress";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import Constants from "../../utility/constants";
import MemoProxy from "../../utility/proxy/manager/memoProxy";
import QrScannerModal from "./qrScannerModal";
import StoneDetailModal from "../common/stoneDetailModal";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import { LoadingButton } from "@mui/lab";
import UserProxy from "../../utility/proxy/manager/userProxy";
import Select from "react-select";

import { alpha, styled } from "@mui/material/styles";
import colourchart from '../../images/ColorScheme.png'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { IoCloudyNightOutline } from "react-icons/io5";
import { IoPartlySunnyOutline } from "react-icons/io5";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { MdOutlineClear } from "react-icons/md";
import PageTitle from "../common/pageTitle";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UserDetailModal from "../operations/userDetailModal";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

const StyledMemoButton = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 100,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      fontSize: 15,
      padding: "4px 12px",
      '& .MuiSvgIcon-root': {
        fontSize: 16,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

const innerTableStyleCSS = {
  '& .MuiDataGrid-columnHeader': {
    color: '#fff',
    backgroundColor: 'grey',
  },
  "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
    py: "4px",
  },
  "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
    py: "12px",
  },
  "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
    py: "18px",
  },

  fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
  "& .super-app-theme--AVAILABLE": {
    color: "black",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    "&:hover": {
      color: "black",
      fontSize: Constants.defaultTableFontSize,
      fontWeight: Constants.defaultFontWeight,
    },
  },
  "& .super-app-theme--HOLD": {
    backgroundColor: "#B6B9D6",
    color: "black",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    "&:hover": {
      backgroundColor: "#B6B9D6",
      color: "black",
      fontSize: Constants.defaultTableFontSize,
      fontWeight: Constants.defaultFontWeight,
    },
  },
  "& .super-app-theme--GREEN_HOLD": {
    color: "#080808",
    backgroundColor: "#B6D7A8",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    "&:hover": {
      color: "#080808",
      backgroundColor: "#B6D7A8",
      fontSize: Constants.defaultTableFontSize,
      fontWeight: Constants.defaultFontWeight,
    },
  },
  "& .super-app-theme--MEMO_OUT": {
    backgroundColor: "#B6B9D6",
    color: "black",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    "&:hover": {
      backgroundColor: "#B6B9D6",
      color: "black",
      fontSize: Constants.defaultTableFontSize,
      fontWeight: Constants.defaultFontWeight,
    },
  },
}

const MemoIssue = ({
  showModal,
  memoid,
  closeModal,
}) => {
  const [memoId, setMemoId] = useState(null);

  const [listData, setListData] = useState([]);

  const [selectionModel, setSelectionModel] = useState([]);
  const [showQr, setShowQr] = useState(false);

  const [memoLoader, setMemoloader] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [scanRfLoad, setScanRfLoad] = useState(false);
  const [extraPacket, setExtraPacket] = useState("");
  const [showextraSnackbar, setshowextraSnackbar] = useState(false);
  const [showselectedSnackbar, setshowselectedSnackbar] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [checkPackages, setCheckPackages] = useState(true);
  const [refresh, setRefresh] = useState(false);
  // eslint-disable-next-line
  const [tags, setTags] = useState([]);
  const [clearTag, setClearTag] = useState(false);

  const [memoIds, setMemoIds] = useState([]);
  const [stoneId, setStoneId] = useState("");
  const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
  const [customer, setCustomer] = useState({});
  const [customerOptions, setCustomerOptions] = useState([]);
  const [searchLoader, setSearchLoader] = useState(false);
  const [isScanEnabled, setIsScanEnabled] = useState(false);
  const [stoneDataMap, setStoneDataMap] = useState({});
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("");

  // Stock Summary
  const [noOfSelectedStones, setNoOfSelectedStones] = useState("0");
  const [selectedTotalSize, setSelectedTotalSize] = useState("0");
  const [selectedAvgDiscount, setSelectedAvgDiscount] = useState("0");
  const [selectedavgPrice, setSelectedavgPrice] = useState("0");
  const [selectedtotalPrice, setSelectedtotalPrice] = useState("0");

  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [flag, setFlag] = useState(false);
  const [isClearTagChecked, setIsClearTagChecked] = useState(false);

  // eslint-disable-next-line
  const [local_Permission, setLocal_Permission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.OPERATION_MEMO_ISSUE
    )
  );

  const [warningForWrongStatusForHoldStones, setWarningForWrongStatusForHoldStones] = useState(false);
  const [warningMsg, setWarningMsg] = useState(false);
  const [columnNames, setColumnNames] = React.useState([]);

  const extraPacketRef = useRef(extraPacket);

  useEffect(() => {
    extraPacketRef.current = extraPacket;
  }, [extraPacket]);

  // Create a connection
  const ws = useRef(null);

  useEffect(() => {
    PageTitle("OPERATION_MEMO_ISSUE");
    if (showModal) {
      setMemoId(memoid);
      handleSearch(memoid);
    } else {
      prepareData();
    }

    prepareColumns();
  }, []);

  useEffect(() => {
    if (showModal && memoId && listData.length > 0 && flag) {
      performRFScan();
    }
  }, [memoId, showModal, flag]);

  useEffect(() => {
    let stockMap = new Map();
    if (listData.length !== 0) {
      listData.forEach(iData => {
        stockMap.set(iData.stoneid, iData);
      })
      setStoneDataMap(stockMap);
    }
  }, [listData])

  const prepareData = () => {
    UserProxy.GetAllUserCustomerMiniByLocation(Constants.loggedInUser.locationid, (response) => {
      setAllCustomerData(response);
    })
  }

  const setAllCustomerData = (response) => {
    let optionsArray = [];
    response.map((data) => {
      let option = { value: data.id, label: data.companyname };
      optionsArray.push(option);
      return true;
    });

    setCustomerOptions(optionsArray);
    setCustomer(optionsArray.length > 0 ? optionsArray[0] : {});
  };

  const getIssuedStones = () => {
    setIsScanEnabled(false);
    setSearchLoader(true);
    MemoProxy.GetIssuedStones(Constants.loggedInUser.locationid, customer.value, (response) => {
      if (response) {
        let sortedResponse = response.sort((a, b) => a.stoneid - b.stoneid);
        let updatedResponse = sortedResponse.map(iData => {
          const today = new Date();
          const nDaysAgo = new Date(today);
          nDaysAgo.setDate(today.getDate() - Constants.NEW_STONE_DAYS);
          const referenceDate = iData.age ? new Date(iData.age) : iData.stonecreatedat ? new Date(iData.stonecreatedat) : null;

          return {
            ...iData,
            daymemo: true,
            isNew: referenceDate ? referenceDate > nDaysAgo : false,
          }
        });

        setListData(updatedResponse);
        loadTotalData(response);
        setSearchLoader(false)
      } else {
        setSearchLoader(false);
        setErrorSnackbar(true);
        setErrorMessage(Constants.APiFailErrorMessage);
      }
    });
  };

  const checkStones = () => {
    let stoneDataArray = [];
    let warningMessage = "";
    selectionModel.map(stoneid => {
      let iData = stoneDataMap.get(stoneid);
      // If iData.stoneoldstatus is not null that means stone is in LOCATION_TRANSFER 
      if (iData.stoneoldstatus !== null) {
        stoneDataArray.push(iData.stoneid);
        warningMessage = "Stone is not Available for Issue. Stone is IN_TRANSIT";
      } else if (iData.stonelocationid !== Constants.loggedInUser.locationid) {
        stoneDataArray.push(iData.stoneid);
        warningMessage = "Stone is not Available for Issue. Stone and Memo Location is Different";
      } else if ((iData.stonestatus !== "AVAILABLE" && iData.stonestatus !== "HOLD") || (iData.stonestatus === "HOLD" && iData.memodetailsubstatus === "MEMO_OUT")) {
        stoneDataArray.push(iData.stoneid);
        warningMessage = "Stone is not Available for Issue. Stone is MEMO_OUT";
      } else if (iData.stonestatus === "HOLD" && (iData.customerid !== iData.holdcustomerid)) {
        stoneDataArray.push(iData.stoneid);
        warningMessage = "Stone is not Available for Issue. Stone is hold for other customer";
      } else if (iData.daymemo === null || iData.daymemo === undefined) {
        stoneDataArray.push(iData.stoneid);
        warningMessage = "Selected stone memo type is empty."
      } else if (iData.stonelabdetailstatus === "REPORT_RECEIVED") {
        stoneDataArray.push(iData.stoneid);
        warningMessage = "Stone is not Available for Issue. Stone is in LAB. Please mark stone as receive and try again later."
      }
      return true;
    });
    if (stoneDataArray.length === 0) {
      prepareMemoIssueData();
    } else {
      setWarningMsg(warningMessage);
      setWarningForWrongStatusForHoldStones(true);
    }
  }

  const prepareMemoIssueData = () => {

    let memoIdsList = memoIds.filter(
      (value, index, array) => array.indexOf(value) === index
    )

    let filteredData = memoIdsList.map((data) => {
      return {
        memoid: data,
        stones: prepareStoneData(data),
        oldstatus: "NEW",
        newstatus: "MEMO_OUT"
      };
    });

    issueMemo(filteredData);
    // return filteredData;
  }

  const prepareStoneData = (data) => {
    let stoneDataArray = [];
    listData.map((iData) => {
      if (iData.memoid === data && selectionModel.includes(iData.stoneid)) {
        stoneDataArray.push({ id: iData.stoneid, status: iData.stonestatus, daymemo: iData.daymemo });
      }
      return stoneDataArray;
    });
    return stoneDataArray;
  };

  const markDayMemo = (dayMemo) => {
    let updatedListData = listData.map(iData => {
      if (selectionModel.includes(iData.stoneid)) {
        return {
          ...iData,
          daymemo: dayMemo
        };
      }
      return iData;
    })

    setListData(updatedListData);
  }

  const handleSearch = (memoid) => {
    let status = "NEW";
    setMemoloader(true);
    setIsScanEnabled(true);
    setShowQr(false);
    MemoProxy.GetAllMemoAtALocationByMemoIdAndMemoStatus(
      Constants.loggedInUser.locationid,
      memoid,
      status,
      (response) => {
        if (response) {

          const today = new Date();
          const nDaysAgo = new Date();
          nDaysAgo.setDate(today.getDate() - Constants.NEW_STONE_DAYS);

          let sortedResponse = response.sort((a, b) => a.stoneid - b.stoneid);
          let updatedResponse = sortedResponse.map(iData => {
            const referenceDate = iData.age
              ? new Date(iData.age)
              : iData.stonecreatedat
                ? new Date(iData.stonecreatedat)
                : null;

            return {
              ...iData,
              daymemo: true,
              isNew: referenceDate ? referenceDate > nDaysAgo : false,
            };
          });

          setListData(updatedResponse);
          loadTotalData(response);
          if (!flag)
            setFlag(true);
          setMemoloader(false);
        } else {
          setMemoloader(false);
          setErrorSnackbar(true);
          setErrorMessage(Constants.APiFailErrorMessage);
        }
      }
    );
  };

  const handleValidation = (data) => {
    //TODO do validation if needed here
    return true;
  };

  const handleStoneIds = (data) => {
    let stoneDataArray = [];
    listData.map((iData) => {
      data.map((da) => {
        if (iData.stoneid === da) {
          stoneDataArray.push(iData.memoid);
        }
        return true;
      });
      return true;
    });
    setMemoIds(stoneDataArray);
    return true;
  };

  const setTagsSelection = (tags) => {
    //tags is array of string which are tag sample ['t1','t2']
    let lStockData = listData;
    let iNewSelected = 0;
    let iUnSelectedTags = 0;
    let selectedCheckBox = [];
    let tagsFound = [];
    let tagsNotFound = [];

    // tags = tags.map(tag => tag.replace(/^\['(.+?)'\]$/, '$1'));

    for (let loopCnt = 0; loopCnt < lStockData.length; loopCnt++) {
      if (lStockData[loopCnt].tag && tags.has(lStockData[loopCnt].tag)) {
        selectedCheckBox.push(lStockData[loopCnt].stoneid);
        tagsFound.push(lStockData[loopCnt].tag);
        iNewSelected++;
      }
    }

    tagsNotFound = [...tags].filter(tag => !tagsFound.includes(tag));
    iUnSelectedTags = tags.size - iNewSelected;

    setListData(() => {
      let arr = [];
      lStockData.map((res) => {
        let option = {
          ...res,
          isChecked: selectedCheckBox.includes(res.stoneid) ? "true" : "false",
        };
        arr.push(option);
        return true;
      });
      return arr;
    });
    setSelectionModel(prev => Array.from(new Set([...prev, ...selectedCheckBox])));
    handleStoneIds(selectedCheckBox);

    if (tagsNotFound.length > 0) {
      setExtraPacket(prev => Array.from(new Set([...prev, ...tagsNotFound])));
    }

    return {
      NewSelectedStones: iNewSelected,
      UnusedTags: iUnSelectedTags,
    };
  };

  const performRFScan = () => {

    if (!ws.current) {
      ws.current = new WebSocket(Constants.RFID_API);
    }

    // Open WebSocket connection and start scanning
    if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
      // WebSocket doesn't exist or was closed — create a new one
      ws.current = new WebSocket(Constants.RFID_API);
      ws.current.onopen = () => {
        ws.current.send("START_SCAN");
        setScanRfLoad(prev => !prev);
      };
    } else if (ws.current.readyState === WebSocket.CONNECTING) {
      // It's still connecting — safely assign onopen
      ws.current.onopen = () => {
        ws.current.send("START_SCAN");
        setScanRfLoad(prev => !prev);
      };
    } else if (ws.current.readyState === WebSocket.OPEN) {
      // Already open, just send
      ws.current.send("START_SCAN");
      setScanRfLoad(prev => !prev);
    }

    // Listen for incoming messages from WebSocket
    let lstTags = new Set();
    ws.current.onmessage = (e) => {
      let message = typeof e.data === 'string' ? e.data : String(e.data);
      if (message.includes("TAG: ")) {
        const parts = message.split("TAG: ");
        if (parts.length === 2) {
          const remaining = parts[1].trim(); // remove extra whitespace
          remaining.split(',').forEach(str => lstTags.add(str.trim())); // split and trim each item
        }

        if (listData.length > 0) {
          setTagsSelection(lstTags);
        }
      } else if (message.includes("SCAN_COMPLETED")) {
        ws.current.close();
        ws.current.onclose = () => {
          getStoneIdByTagId();
        }
        setScanRfLoad(prev => !prev);
      }
    }
  };

  const getStoneIdByTagId = () => {
    if (extraPacketRef.current.length > 0) {
      StoneProxy.GetStoneIdByTagId(extraPacketRef.current, (response) => {
        if (response) {
          let resultString = Object.entries(response)
            .map(([stoneid, tag]) => `${stoneid}: ${tag}`)
            .join(', ');

          if (Object.entries(response).length < extraPacketRef.current.length) {
            const usedTags = new Set(Object.values(response));
            const extraTags = extraPacketRef.current.filter(tag => !usedTags.has(tag));

            const extraTagString = extraTags.map(tag => `STONE_ID: ${tag}`).join(', ');
            resultString = resultString + ', ' + extraTagString;
          }

          setShowSnackbar(true);
          setSuccessMessage(`You have ${resultString} extra package on your RF Machine`);
          setSnackbarVariant("error");
          setExtraPacket([]);
          ws.current = null;
        } else {
          setShowSnackbar(true);
          setSuccessMessage(Constants.APiFailErrorMessage);
          setSnackbarVariant("error");
          ws.current = null;
          setExtraPacket([]);
        }
      })
    }
  }

  const stopRFScan = () => {
    setScanRfLoad(false);
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.close();
      ws.current.onclose = () => {
        getStoneIdByTagId();
      }
    }
  };

  const findMemoByTags = (tags) => {
    let data = {
      status: "NEW",
      tags: tags
    };
    setScanRfLoad(true);
    MemoProxy.FindMemoByTags(Constants.loggedInUser.locationid, data, (response) => {
      if (response) {
        setListData(response);
        setRefresh(true);
        setScanRfLoad(false);
        if (checkPackages) {
          if (tags.length > response.length) {
            setExtraPacket(tags.length - response.length);
            setshowextraSnackbar(true);
            setCheckPackages(false);
          }
        }
      } else {
        setErrorSnackbar(true);
        setErrorMessage(Constants.APiFailErrorMessage);
        setScanRfLoad(false);
      }
    });
  };

  const issueMemo = (data) => {
    setLoadingButton(true);

    MemoProxy.SendMultipleMemo(Constants.loggedInUser.locationid, data, clearTag, (responseData) => {
      if (responseData && responseData.status === "Success") {
        refresh
          ? findMemoByTags(tags)
          : showModal ? closeModal("Stones Issued Successfully", ws)
            : handleSearch(memoId);
        setLoadingButton(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Stones Issued Successfully");
      } else {
        setErrorSnackbar(true);
        setErrorMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setLoadingButton(false);
      }
    });
  };

  const handleStoneDetailModal = (params) => {
    setStoneId(params.value);
    setShowStoneDetailModal(true);
  };

  const handleClose = () => {
    setShowStoneDetailModal(false);
    setShowSuccessSnackbar(false);
  };

  const loadTotalData = (data) => {
    let totalSize = 0;
    let totalPrice = 0;   //ppc*size
    let total = 0;       //rapnetprice*size

    data.forEach((iData) => {
      let pricePerCarat = iData.price
      totalSize += iData.size;
      totalPrice += (iData.size * pricePerCarat);
      total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));

    });
    setNoOfSelectedStones(data.length);
    setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
    setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
    setSelectedavgPrice(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          totalPrice / totalSize
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
    setSelectedAvgDiscount(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          (100 - (totalPrice / total) * 100) * -1
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
  };

  useEffect(() => {
    if (selectionModel.length <= 0) {
      loadTotalData(listData);
      return;
    }
    let totalSize = 0;
    let totalPrice = 0;   //ppc*size
    let total = 0;       //rapnetprice*size
    listData.forEach((iData) => {
      if (selectionModel.includes(iData.stoneid)) {
        let pricePerCarat = iData.price
        totalSize += iData.size;
        totalPrice += (iData.size * pricePerCarat);
        total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));
      }
    });
    setNoOfSelectedStones(selectionModel.length);
    setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
    setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
    setSelectedavgPrice(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          totalPrice / totalSize
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
    setSelectedAvgDiscount(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          (100 - (totalPrice / total) * 100) * -1
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );

  }, [selectionModel]);

  const columnData = {
    "8eaa8a03-d163-436d-8912-51ea06306f44": {
      field: "certificateid",
      headerName: "CERT",
      headerAlign: "center",
      align: "center",
      width: 100,
      renderCell: renderCellExpand,
    },
    "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
      field: "stonetype",
      headerName: "STONETYPE",
      headerAlign: "center",
      align: "center",
      width: 100,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
      filterOperators: Utility.combinedFilters(),
      valueGetter: (params) => Constants.STONE_TYPE[params.value],
      renderCell: (params) => Constants.STONE_TYPE[params.value],
    },
    "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
      field: "shape",
      headerName: "SHAPE",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 80,
      renderCell: renderCellExpand,
    },
    "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
      field: "stoneid",
      headerName: "STONEID",
      headerAlign: "center",
      align: "center",
      type: "number",
      width: 110,
      renderCell: (params) => {
        const isNew = params.row.isNew;
        const shouldShowModal = Utility.NeedToShowStoneDetailModal();
        if (isNew) {
          return (
            <Box
              sx={{
                "& .MuiBadge-badge": {
                  height: "0.75rem !important",
                  padding: "2px 4px !important",
                  fontSize: "10px",
                  top: "9px",
                  backgroundColor: "#E0FBE2 !important",
                  color: "#059212 !important",
                },
              }}
            >
              <Badge
                badgeContent={"NEW"}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <Button
                  sx={{
                    textDecorationLine: "underline",
                    color: "black",
                  }}
                  onClick={() => handleStoneDetailModal(params)}
                >
                  {params.value}
                </Button>
              </Badge>
            </Box>
          );
        }

        if (shouldShowModal) {
          return (
            <Button
              sx={{
                textDecorationLine: "underline",
                color: "black",
              }}
              onClick={() => handleStoneDetailModal(params)}
            >
              {params.value}
            </Button>
          );
        }

        return <div>{params.value}</div>;
      },
    },
    "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
      field: "stonestatus",
      headerName: "STONE STATUS",
      headerAlign: "center",
      align: "center",
      width: 150,
      renderCell: (params) => params.value === "HOLD" ? (
        <span style={{ fontSize: "14px" }}>
          <b>{params.value}</b>{
            params.row.memodetailsubstatus === "MEMO_OUT" ? (<>&nbsp;|&nbsp;MEMO_OUT</>) :
              (params.row.stonelabdetailstatus === "REPORT_RECEIVED" ? (<>&nbsp;|&nbsp;LAB</>) : null)
          }
        </span>
      ) : (params.row.stonelabdetailstatus === "REPORT_RECEIVED" ? "LAB" : params.value),
      cellClassName: (params) => params.row.stonelabdetailstatus === "REPORT_RECEIVED" ? "greenCell" :
        (params.row.stonestatus === "HOLD" ? (params.row.holduserid === params.row.userid && params.row.holdcustomerid === params.row.customerid ? `super-app-theme--GREEN_HOLD` : `super-app-theme--HOLD`) : `super-app-theme--${params.row.stonestatus.toUpperCase()}`),
    },
    "ab3e506e-bddb-456d-b967-922dcee74d56": {
      field: "location",
      headerName: "LOCATION",
      headerAlign: "center",
      align: "left",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 130,
      renderCell: (params) => !(params.row.stoneoldstatus !== null || params.row.stonelocationid !== Constants.loggedInUser.locationid) ? params.value : (
        <Box
          sx={{
            "& .MuiBadge-badge": {
              height: "0.75rem !important",
              padding: "2px 4px !important",
              fontSize: "8px",
              top: "-1px",
              right: "-20px",
              backgroundColor: "#E5D9F2 !important",
              color: "#624E88 !important",
              fontWeight: "700"
            },
          }}
        >
          <Badge
            badgeContent={"TRANSIT"}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {params.value}
          </Badge>
        </Box>
      ),
    },
    "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
      field: "size",
      headerName: "SIZE",
      headerAlign: "center",
      align: "right",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 40,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size),
    },
    "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
      field: "color",
      headerName: "COLOR",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 65,
      renderCell: renderCellExpand,
    },
    "d4992de9-92ab-4f9e-ba12-6c746e935611": {
      field: "colordescriptions",
      headerName: "COLOR DESCRIPTIONS",
      headerAlign: "center",
      align: "center",
      width: 160,
      renderCell: renderCellExpand,
    },
    "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
      field: "clarity",
      headerName: "CLARITY",
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      align: "center",
      width: 75,
      renderCell: renderCellExpand,
    },
    "3597803f-8932-40c6-8de6-81006f4d6d5b": {
      field: "cut",
      headerName: "CUT",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 50,
      renderCell: renderCellExpand,
    },
    "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
      field: "pol",
      headerName: "POL",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 40,
      renderCell: renderCellExpand,
    },
    "5541c182-21b8-496e-b342-b0b89f3d302f": {
      field: "sym",
      headerName: "SYM",
      headerAlign: "center",
      align: "center",
      width: 40,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "8296e627-0be5-4a66-830f-ea6867cea137": {
      field: "flo",
      headerName: "FLO",
      headerAlign: "center",
      align: "center",
      width: 60,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "798a8663-0f0c-41be-8512-16dd60d5f25e": {
      field: "discount",
      headerName: "DIS",
      headerAlign: "center",
      align: "center",
      width: 50,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
    },
    "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
      field: "rapnetprice",
      headerName: "RAP",
      headerAlign: "center",
      align: "center",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.rapnetprice)
    },
    "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
      field: "lab",
      headerName: "LAB",
      headerAlign: "center",
      align: "center",
      width: 40,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "39c634f1-2644-4307-acbb-983b4de4229e": {
      field: "price",
      headerName: "PPC",
      headerAlign: "center",
      align: "center",
      width: 70,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
    },
    "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
      field: "total",
      headerName: "TOTAL",
      headerAlign: "center",
      align: "center",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
    },
    "54fbd914-6ce7-44a8-9354-db6125ba1992": {
      field: "tag",
      headerName: "TAG",
      headerAlign: "center",
      align: "center",
      width: 100,
      renderCell: (params) => (!params.value) ? "" : params.value,
    },
  };

  const prepareColumns = () => {
    let columnNames = [];

    columnNames.push({
      field: "memoid",
      headerName: "MEMO ID",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 80,
    });

    Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
      if (columnData.hasOwnProperty(iColPermission)) {
        const columnsData = columnData[iColPermission];
        columnNames.push(columnsData);
        if (columnsData.field === "stoneid") {
          columnNames.push({
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 65,
            renderCell: renderCellExpand,
          });
          columnNames.push({
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            type: "dateTime",
            valueGetter: (params) => {
              if (params.value) {
                return new Date(params.value);
              }
              return null;
            },
            width: 150,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
          });
          columnNames.push({
            field: "username",
            headerName: "CREATED BY",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
          });
          columnNames.push({
            field: "customercompanyname",
            headerName: "CUSTOMER COMPANY",
            headerAlign: "center",
            align: "center",
            width: 170,
            renderCell: (params) =>
              params.row.customerid + " | " + params.row.customercompanyname,
          });
          columnNames.push({
            field: "daymemo",
            headerName: "TYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
            valueGetter: (params) => params.row.daymemo === null ? "" : (params.row.daymemo ? "DAY" : "OVERNIGHT"),
            renderCell: (params) => params.row.daymemo === null ? "" : (params.row.daymemo ? "DAY" : "OVERNIGHT"),
            cellClassName: 'lavendarColor',
            type: "singleSelect",
            valueOptions: ["DAY", "OVERNIGHT"],
            filterOperators: Utility.combinedFilters(),
          });
          columnNames.push({
            field: "status",
            headerName: "MEMO STATUS",
            headerAlign: "center",
            align: "center",
            width: 170,
            renderCell: renderCellExpand,
          });
          columnNames.push({
            field: "memodetailstatus",
            headerName: "MEMO DETAIL STATUS",
            headerAlign: "center",
            align: "center",
            width: 150,
            renderCell: renderCellExpand,
          });
        } else if (columnsData.field === "stonestatus") {
          columnNames.push({
            field: "stonesubstatus",
            headerName: "STONE SUB STATUS",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: (params) => params.row.stoneoldstatus !== null ? "IN_TRANSIT" : (params.row.stonelocationid !== Constants.loggedInUser.locationid ? "LOCATION CHANGED" : (params.row.memodetailsubstatus === "MEMO_OUT" ? "MEMO_OUT" : " - ")),
            valueGetter: (params) => params.row.stoneoldstatus !== null ? "IN_TRANSIT" : (params.row.stonelocationid !== Constants.loggedInUser.locationid ? "LOCATION CHANGED" : (params.row.memodetailsubstatus === "MEMO_OUT" ? "MEMO_OUT" : " - ")),
            cellClassName: (params) => params.row.memodetailsubstatus === "MEMO_OUT" ? `super-app-theme--MEMO_OUT` : ""
          });
        }
      }
    })

    columnNames.push({
      field: "memomasterremark",
      headerName: "MEMO REMARK",
      headerAlign: "center",
      align: "center",
      width: 130,
      renderCell: renderCellExpand,
    });
    columnNames.push({
      field: "memodetailremark",
      headerName: "STONE REMARK",
      headerAlign: "center",
      align: "center",
      width: 130,
      renderCell: renderCellExpand,
    });
    columnNames.push({
      field: "lastupdatedat",
      headerName: "LAST UPDATED",
      headerAlign: "center",
      align: "center",
      width: 150,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    });
    columnNames.push({
      field: "expectedreceive",
      headerName: "EXPECTED RECEIVE",
      headerAlign: "center",
      align: "center",
      width: 150,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    });
    columnNames.push({
      field: "updatedat",
      headerName: "UPDATED AT",
      headerAlign: "center",
      align: "center",
      width: 150,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    });
    columnNames.push({
      field: "givenat",
      headerName: "GIVEN AT",
      headerAlign: "center",
      align: "center",
      width: 150,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    });

    setColumnNames(columnNames);
  }

  const rows = listData;
  const columns = columnNames;

  const CustomToolbar = ({ isClearTagChecked, setIsClearTagChecked }) => {
    const [markMenu, setMarkMenu] = useState(undefined);
    const [colorChartMenu, setColorChartMenu] = useState(undefined);
    const handleCloseButton = () => {
      setColorChartMenu(null);
    };
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          alignItems: "center",
          marginHorizontal: "5px",
          "&.MuiDataGrid-toolbarContainer": {
            padding: "0px 4px !important",
          },
        }}
      >
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExport />

        <Checkbox
          checked={isClearTagChecked}
          color="success"
          onChange={(e) => {
            setIsClearTagChecked(e.target.checked);
            setClearTag((prev) => !prev)
          }}
          size="small"
        />
        <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>Clear Tag</Typography>

        <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />
        {listData.length > 0 && (
          <Box>
            {memoLoader ? (
              <CircularProgress size="1rem" />
            ) : (
              <Tooltip title="Reload List">
                <RefreshOutlinedIcon
                  onClick={() =>
                    refresh
                      ? findMemoByTags(tags)
                      : memoId ? handleSearch(memoId) : getIssuedStones()
                  }
                  color="primary"
                  fontSize="medium"
                  sx={{ cursor: "pointer" }}
                />
              </Tooltip>
            )}
          </Box>
        )}
        <Box >
          {!showModal ? <LoadingButton
            loading={memoLoader}
            loadingPosition="start"
            variant="contained"
            component="label"
            size="small"
            startIcon={<QrCodeScannerIcon size="large" />}
            onClick={() => {
              setShowQr(true);
            }}
          >
            Scan
          </LoadingButton> : null}
          {showQr ? (
            <QrScannerModal
              show={showQr}
              setShow={setShowQr}
              handleValidation={handleValidation}
              handleSave={handleSearch}
              stoneId={selectionModel}
              sourceData={listData}
              setMemoId={setMemoId}
            />
          ) : null}
        </Box>

        <Box>
          <Tooltip title="Scan RFids">
            <LoadingButton
              variant="contained"
              component="label"
              size="small"
              loading={scanRfLoad}
              loadingPosition="start"
              startIcon={<QrCodeScannerIcon size="large" />}
              color="success"
              onClick={() => performRFScan()}
              disabled={!scanRfLoad && memoId ? false : true}
            >
              Scan RF
            </LoadingButton>
          </Tooltip>
        </Box>

        <Box>
          <Tooltip title="Stop Scan">
            <Button
              variant="contained"
              component="label"
              size="small"
              loadingPosition="start"
              startIcon={<QrCodeScannerIcon size="large" />}
              color="error"
              onClick={() => stopRFScan()}
              disabled={!scanRfLoad}
            >
              Stop Scan
            </Button>
          </Tooltip>
        </Box>

        {Utility.isUpdateAllowed(local_Permission) ? (
          <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} >
            <Box>
              <Button
                id="demo-customized-memo-button"
                aria-controls={markMenu ? 'demo-customized-memo-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={markMenu ? 'true' : undefined}
                variant="outlined"
                disableElevation
                disabled={(selectionModel.length > 0 && isScanEnabled) ? false : true}
                onClick={(e) => setMarkMenu(e.currentTarget)}
                sx={{
                  color: '#2e7d32',
                  borderRadius: "4px",
                  border: "1px solid rgba(46, 125, 50, 0.5)",
                  padding: "2px 2px 2px 8px",
                  cursor: "pointer"
                }}
              >
                <CheckCircleOutlineIcon size={18} />
                <span style={{ fontSize: "14px", marginLeft: "8px" }}>Mark As</span>
                <KeyboardArrowDownIcon />
              </Button>
              <StyledMemoButton
                id="demo-customized-memo-menu"
                MenuListProps={{
                  'aria-labelledby': 'demo-customized-memo-button',
                  style: {
                    maxHeight: 250,
                  },
                }}
                anchorEl={markMenu}
                open={markMenu}
                onClose={() => setMarkMenu(null)}
              >
                <MenuItem onClick={() => {
                  markDayMemo(true);
                  setMarkMenu(null);
                }} disableRipple>
                  <IoPartlySunnyOutline size={18} /><span style={{ marginLeft: "5px" }}>Day Memo</span>
                </MenuItem>

                <MenuItem onClick={() => {
                  markDayMemo(false);
                  setMarkMenu(null);
                }} disableRipple>
                  <IoCloudyNightOutline size={18} /><span style={{ marginLeft: "5px" }}>Overnight Memo</span>
                </MenuItem>

                <MenuItem onClick={() => {
                  markDayMemo(null);
                  setMarkMenu(null);
                }} disableRipple>
                  <MdOutlineClear size={18} /><span style={{ marginLeft: "5px" }}>Clear</span>
                </MenuItem>
              </StyledMemoButton>
            </Box>
            <Box sx={{ marginLeft: "15px" }}>
              <Tooltip title="Issue Stones">
                <LoadingButton
                  variant="contained"
                  component="label"
                  size="small"
                  startIcon={<DiamondOutlinedIcon size="large" />}
                  color="success"
                  disabled={(selectionModel.length > 0 && isScanEnabled) ? false : true}
                  onClick={() => checkStones()}
                  loading={loadingButton}
                >
                  Issue
                </LoadingButton>
              </Tooltip>
            </Box>
          </Box>
        ) : null}
        <div>
          <Box
            id="demo-customized-button"
            aria-controls={colorChartMenu ? 'demo-customized-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={colorChartMenu ? 'true' : undefined}
            variant="outlined"
            disableElevation
            onClick={(e) => setColorChartMenu(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              color: '#454545',
              borderColor: '#454545',
              borderRadius: "4px",
              border: "1px solid black",
              padding: "2px 0 2px 5px",
              cursor: "pointer"
            }}
          >
            <img
              src={colourchart}
              alt="Color Scheme"
              style={{
                height: '24px',
                width: '24px',
              }}
            />
            <KeyboardArrowDownIcon />
          </Box>
          <StyledMenu
            id="demo-customized-menu"
            MenuListProps={{
              'aria-labelledby': 'demo-customized-button',
              style: {
                maxHeight: 250
              },
            }}
            anchorEl={colorChartMenu}
            open={colorChartMenu}
            onClose={handleCloseButton}
          >
            <MenuItem onClick={handleCloseButton} disableRipple>
              <span style={{ height: "15px", width: "15px", backgroundColor: "#000", borderRadius: 20, margin: 10 }}></span>AVAILABLE
            </MenuItem>
            <MenuItem onClick={handleCloseButton} disableRipple>
              <span style={{ height: "15px", width: "15px", backgroundColor: "#b6b9d6", borderRadius: 20, margin: 10 }}></span><b>HOLD</b>&nbsp;/ MEMO_OUT (Another Customer)
            </MenuItem>
            <MenuItem onClick={handleCloseButton} disableRipple>
              <span style={{ height: "15px", width: "15px", backgroundColor: "#B6D7A8", borderRadius: 20, margin: 10 }}></span><b>HOLD</b>&nbsp;(My Hold)
            </MenuItem>
            <MenuItem onClick={handleCloseButton} disableRipple>
              <span style={{ height: "15px", width: "15px", backgroundColor: "#FEBD7D", borderRadius: 20, margin: 10 }}></span>IN_TRANSIT/ LOCATION CHANGED
            </MenuItem>
          </StyledMenu>
        </div>
        <GridToolbarQuickFilter debounceMs={500} />
      </GridToolbarContainer>
    )
  }

  return (
    <div>
      {showStoneDetailModal ? (
        <StoneDetailModal
          show={showStoneDetailModal}
          stoneId={stoneId}
          handleClose={handleClose}
        />
      ) : null}

      {showUserDetailModal ? (
        <UserDetailModal
          show={true}
          handleClose={() => setShowUserDetailModal(false)}
          handleSelect={(selectedOption) => {
            const selectedCustomers = customerOptions.filter(opt =>
              selectedOption.includes(opt.value)
            );
            setCustomer(selectedCustomers);
            setShowUserDetailModal(false)
          }}
          userids={customerOptions.map(cust => cust.value)}
          selectedIds={
            Object.keys(customer).length !== 0
              ? Object.values(customer).map(iCustomer => iCustomer.value)
              : []
          }
          usertype={"C"}
          loading={showUserDetailModal}
          isMulti={false}
        />
      ) : null}

      {warningForWrongStatusForHoldStones ? (
        <TabMenuSnackbar
          show={true}
          message={warningMsg}
          variant={"warning"}
          autoHide={false}
          title={"Warning"}
          handleClose={() => setWarningForWrongStatusForHoldStones(false)}
        />
      ) : null}
      <TabMenuSnackbar
        show={showextraSnackbar}
        message={
          <Box>You have {extraPacket} extra package on your RF Machine</Box>
        }
        variant={"error"}
        autoHide={false}
        handleClose={() => setshowextraSnackbar(false)}
        title="Error"
      />

      <TabMenuSnackbar
        show={showselectedSnackbar}
        message={<Box>Stones selected successfully</Box>}
        variant={"success"}
        autoHide={Constants.SnackBarCloseTimer}
        handleClose={() => setshowselectedSnackbar(false)}
        title="Success"
      />

      <TabMenuSnackbar
        show={showSuccessSnackbar}
        message={successMessage}
        variant={"success"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Success"}
        handleClose={handleClose}
      />

      <TabMenuSnackbar
        show={showSnackbar}
        message={successMessage}
        variant={snackbarVariant}
        autoHide={Constants.SnackBarCloseTimer}
        title={snackbarVariant === "success" ? "Success" : "Failed"}
        handleClose={() => setShowSnackbar(false)}
      />

      <TabMenuSnackbar
        show={errorSnackbar}
        message={errorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Failed"}
        handleClose={() => setErrorSnackbar(false)}
      />

      <Box
        m={1}
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        gap={2}
      >
        {customerOptions.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: "15px",
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  mr: { sm: "13px" },
                  mb: { xs: 1, sm: 0 },
                }}
              >
                Customer
              </Typography>

              <Box
                sx={{
                  minWidth: "280px",
                  maxWidth: "450px",
                  width: "100%",
                  "& .css-13cymwt-control": {
                    minHeight: "38px !important",
                  },
                  "& .css-1xc3v61-indicatorContainer": {
                    padding: "6px",
                  },
                }}
              >
                <Select
                  menuPlacement="auto"
                  blurInputOnSelect={false}
                  className="basic-single"
                  classNamePrefix="select"
                  value={customer}
                  isSearchable
                  name="Customer"
                  menuPortalTarget={document.body}
                  styles={Constants.Styles}
                  options={customerOptions}
                  onChange={(selectedOption) => setCustomer(selectedOption)}
                />
              </Box>

              <Tooltip title="View More Data">
                <span
                  style={{
                    cursor: "pointer",
                    marginLeft: "15px",
                    marginTop: "3px",
                  }}
                >
                  <OpenInNewIcon
                    fontSize="medium"
                    color="action"
                    onClick={() => setShowUserDetailModal(true)}
                  />
                </span>
              </Tooltip>
            </Box>

            <LoadingButton
              variant="contained"
              onClick={() => getIssuedStones()}
              startIcon={<SearchOutlinedIcon size="large" />}
              loading={searchLoader}
              loadingPosition="start"
              disabled={customer.length === 0}
              sx={{
                padding: "4px 14px !important",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Search
            </LoadingButton>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            alignItems: { sm: "center" },
            color: "black",
            fontWeight: "normal",
            fontSize: Constants.defaultTableFontSize,
            gap: { xs: "4px", sm: "12px" },
          }}
        >
          <Box><span>No of Stones: </span> <b>{noOfSelectedStones}</b></Box>
          <Box><span>Total Weight: </span> <b>{selectedTotalSize}</b></Box>
          <Box><span>Total Price: </span> <b>{selectedtotalPrice}</b></Box>
          <Box><span>Avg Price: </span> <b>{selectedavgPrice}</b></Box>
          <Box><span>Avg Discount: </span> <b>{selectedAvgDiscount}</b></Box>
        </Box>
      </Box>


      <Box
        sx={{
          backgroundColor: '#fff',
          color: '#000',
          width: "100%",
          position: "relative",
          "& .MuiTablePagination-selectLabel": {
            marginTop: "15px",
          },
          "& .MuiTablePagination-displayedRows": {
            marginTop: "15px",
          },
          "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
            fontWeight: Constants.defaultFontWeight,
          },
          "& .css-cc8tf1": {
            fontWeight: Constants.defaultFontWeight,
          },
          height: "70vh",
          "& .lavendarColor": {
            backgroundColor: "#D8BFD8"
          },
          "& .greenCell": {
            backgroundColor: "#B5DB53"
          }
        }}
      >
        <DataGridPro
          disableRowSelectionOnClick={true}
          sx={innerTableStyleCSS}
          density="compact"
          slots={{ toolbar: CustomToolbar }}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.stoneid}
          checkboxSelection={true}
          onRowSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
            handleStoneIds(newSelectionModel);
          }}
          loading={memoLoader}
          rowSelectionModel={selectionModel}
          initialState={{
            columns: {
              columnVisibilityModel: {
                // Hide columns id, the other columns will remain visible
                updatedat: false,
                lastupdatedat: false,
                memodetailstatus: false,
                status: false,
                stonesubstatus: false
              },
            },
            pagination: {
              paginationModel: Constants.pageSize,
            },
          }}
          pageSizeOptions={Constants.pageSizeOptions}
          pagination
          slotProps={{
            toolbar: {
              isClearTagChecked,
              setIsClearTagChecked
            },
          }}
        />
      </Box>
    </div >
  );
};

export default MemoIssue;
